import { useState, useRef, useEffect } from 'react';
import { Modal, Box, Button, TextField, Typography } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

const ScreenshotModal = ({ open, onClose, defaultUrl, onCaptured, botId }) => {
  const { t } = useTranslation();
  const [url, setUrl] = useState(defaultUrl || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const openRef = useRef(open);
  useEffect(() => { openRef.current = open; }, [open]);
  useEffect(() => {
    setUrl(defaultUrl || '');
  }, [defaultUrl, open]);

  const handleCapture = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}capture_screenshot?url=${encodeURIComponent(url)}&bot_id=${encodeURIComponent(botId)}`);
      if (!res.ok) throw new Error('Request failed');
      const data = await res.json();
      const desktopUrl = data['desktop-url'];
      const mobileUrl = data['mobile-url'];
      if (!desktopUrl && !mobileUrl) throw new Error('No URL returned');
      if (!openRef.current) return;
      onCaptured(desktopUrl || mobileUrl);
      onClose();
    } catch (e) {
      setError(t('screenshot_capture_failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'white',
          borderRadius: '10px',
          boxShadow: 24,
          p: 2.5,
          width: isMobile ? '85%' : '90%',
          maxWidth: 520,
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: '10px' }}>{t('widget_take_new_screenshot')}</h3>

        <TextField
          fullWidth
          label={t('screenshot_url_label')}
          variant="outlined"
          value={url}
          onChange={e => setUrl(e.target.value)}
          margin="normal"
          required
          sx={{
            '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#515151' },
            '& .MuiInputLabel-root': { color: 'gray', '&.Mui-focused': { color: '#5616ea' } },
          }}
        />

        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <FontAwesomeIcon icon={faSpinner} spin />
            <Typography variant="body2">{t('screenshot_taking_progress')}</Typography>
          </Box>
        )}

        {!!error && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            disabled={loading}
            sx={{
              color: '#5616ea',
              borderColor: '#5616ea',
              '&:hover': { borderColor: '#5e0fb3', backgroundColor: '#f3eaff' },
            }}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleCapture}
            variant="contained"
            disabled={loading || !url}
            sx={{ backgroundColor: '#5616ea', '&:hover': { backgroundColor: '#4a14d6' } }}
          >
            {t('screenshot_capture_btn')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ScreenshotModal;
